/**
 * Group Panel Component
 * Displays current group information
 */

import { useMScaleStore } from '../../store'
import { Card } from '../Common/Card'
import { formatWeight } from '../../utils/format'

const GROUP_COLORS = [
  { name: 'Green', bg: '#00FF00', text: '#000000' },
  { name: 'Blue', bg: '#0000FF', text: '#FFFFFF' },
  { name: 'Yellow', bg: '#FFFF00', text: '#000000' },
  { name: 'Red', bg: '#FF0000', text: '#FFFFFF' },
]

export function GroupPanel() {
  const { groups, groupCount, targetRange, groupSize, showHundredthsUnder10g } = useMScaleStore()

  // Determine if we should use "Pairs" or "Groups" terminology
  const isPairMode = groupSize === 2
  const groupLabel = isPairMode ? 'Pair' : 'Group'
  const groupsLabel = isPairMode ? 'Pairs' : 'Groups'

  return (
    <Card title={`Active ${groupsLabel}`}>
      {groupCount === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-2">⚪</div>
          <div className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            No {groupsLabel} Found
          </div>
          <div className="text-sm text-gray-500 dark:text-industrial-text-dim">
            Target range: {targetRange.min.toFixed(1)}g - {targetRange.max.toFixed(1)}g
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {groupCount} {groupCount === 1 ? groupLabel : groupsLabel}
            </div>
            <div className="text-sm text-gray-500 dark:text-industrial-text-dim">
              Target: {targetRange.min.toFixed(1)}-{targetRange.max.toFixed(1)}g
            </div>
          </div>

          {groups?.filter(group => group.isValid).map((group) => {
            const color = GROUP_COLORS[group.groupId]
            const isInRange = group.totalWeight >= targetRange.min && group.totalWeight <= targetRange.max

            return (
              <div
                key={group.groupId}
                className="p-4 rounded-lg border-2"
                style={{
                  borderColor: color.bg,
                  backgroundColor: `${color.bg}15`,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: color.bg }}
                    />
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {color.name}
                    </span>
                  </div>
                  <div className="font-mono font-bold text-lg text-gray-900 dark:text-white">
                    {formatWeight(group.totalWeight, showHundredthsUnder10g)}g
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {group.positions.map((position, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-industrial-text-dim">
                        Pos {position}:
                      </span>
                      <span className="font-mono font-medium text-gray-900 dark:text-white">
                        {formatWeight(group.weights[idx] ?? 0, showHundredthsUnder10g)}g
                      </span>
                    </div>
                  ))}
                </div>

                {!isInRange && (
                  <div className="mt-2 text-xs text-yellow-700 dark:text-yellow-400">
                    ⚠ Outside target range
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}
